"use client";
import React from "react";
import Table from "@/components/primitives/table";
import { ColumnDef } from "@tanstack/react-table";
import { GameAlignmentType, GameSchedule, LineType, Sport, buildOdd } from "common";
import { formatDate, formatTime, getLocalISOFromUTC } from "@/utils";
import { createGameDetailSlug, createTeamSlug } from "@/utils/url";
import GameTableScore from "../game/table/score";
import { empty } from "@/utils/empty";
import GameTablePerspectiveVersus from "../game/table/perspective-versus";
import LiveIndicator from "../game/live-indicator";
import GameTableOddOutcome from "../game/table/odd-outcome";
import Link from "next/link";
import GameTableOdd from "../game/table/odd";

export default function TeamScheduleTable({
  data,
  sport,
}: {
  data: GameSchedule[];
  sport: Sport;
}) {
  const [processedData, setProcessedData] = React.useState(convertTime(data));
console.log("schedule", processedData);
  function convertTime(data: GameSchedule[]): GameSchedule[] {
    return data.map((row) => ({
      ...row,
      dateTime: row.dateTime
        ? formatDate(row.dateTime, true, true, true)
        : "N/A",
      time: row.dateTime
        ? formatTime(getLocalISOFromUTC(row.dateTime), true)
        : "N/A",
    }));
  }

  React.useEffect(() => {
    setProcessedData(convertTime(data));
  }, [data]);
  const columns: ColumnDef<GameSchedule>[] = React.useMemo(
    () => [
      {
        id: "date",
        header: "date",
        width: "124px",
        accessorKey: "dateTime",
        enableSorting: false,
        cell: (info) => {
          return (
            <div className="flex flex-col gap-0.5">
              <span>{`${info.getValue()}`}</span>
              <span className="text-label-sm-medium text-secondary">
                {info.row.original.ifNecessary && "(IF NECESSARY)"}
              </span>
            </div>
          );
        },
      },
      {
        id: "opponent",
        header: "opponent",
        size: 120,
        enableSorting: false,

        cell: (info) => {
          const row = info.row.original;
          return (
            <GameTablePerspectiveVersus
              home={row.home}
              opponent={row.opponent}
              slug={createTeamSlug(sport, row.opponent)}
            />
          );
        },
      },
      ...(data[0] && data[0].score
        ? [
            {
              id: "score",
              header: "score",
              enableSorting: false,
              size: 90,
              cell: (info: any) => {
                const row = info.row.original;
                return (
                  <GameTableScore
                    score={row.score}
                    home={row.home}
                    slug={createGameDetailSlug(sport, row.gameId)}
                    relative
                    overtime={row.overtime}
                    status={row.status}
                  />
                );
              },
            },
          ]
        : [
            {
              id: "time",
              header: "time",
              size: 90,
              accessorKey: "time",
              enableSorting: false,

              cell: (info: any) => {
                if (info.row.original.isLive) {
                  return (
                    <Link
                      href={createGameDetailSlug(
                        sport,
                        info.row.original.gameId
                      )}
                    >
                      <LiveIndicator />
                    </Link>
                  );
                }
                return <Link href={createGameDetailSlug(sport, info.row.original.gameId)}>{info.getValue()}</Link>;
              },
            },
            {
              header: "network",
              accessorKey: "network",
              enableSorting: false,
              size: 90,
            },
          ]),

      {
        id: "moneyline",
        header: "moneyline",
        enableSorting: false,
        size: 90,
        cell: (info) => {
          const row = info.row.original;
          const odd = row.moneyline ? row.moneyline : undefined;
          return <GameTableOddOutcome odd={odd ? {type: LineType.Moneyline, alignment: row.home ? GameAlignmentType.Home : GameAlignmentType.Away, payout: odd.value, outcome: odd.outcome} : undefined} />;
        },
      },
      {
        id: "spread",
        header: "spread",
        enableSorting: false,

        size: 90,
        cell: (info) => {
          const row = info.row.original;
          const odd = row.spread ? row.spread : undefined;
          return <GameTableOddOutcome odd={odd ? {type: LineType.Spread, alignment: row.home ? GameAlignmentType.Home : GameAlignmentType.Away, value: odd.value, payout: odd.payout, outcome: odd.outcome} : undefined} />;
        },
      },
      {
        id: "total",
        header: "total (o/u)",
        minWidth: "80px",
        enableSorting: false,

        size: 90,
        cell: (info) => {
          const row = info.row.original;
          const odd = row.total ? row.total : undefined;
          return <GameTableOddOutcome odd={odd ? {type: LineType.Total, alignment: row.total?.type === "over" ? GameAlignmentType.Away : GameAlignmentType.Home, value: odd.value, payout: odd.payout, outcome: odd.outcome} : undefined} />;
        },
      },
    ],
    []
  );
  return <Table columns={columns} data={processedData} type="banded" />;
}
